import { useState, useEffect, useCallback } from 'react';
import {
  languageUtils,
  getTranslation,
  getTranslationArray,
  isLanguageSelectionEnabled,
  type SupportedLanguage,
} from '@/utils/languageUtils';

export const useLanguage = () => {
  const [currentLanguage, setCurrentLanguageState] = useState<SupportedLanguage>(
    languageUtils.getCurrentLanguage()
  );

  useEffect(() => {
    if (!isLanguageSelectionEnabled) {
      setCurrentLanguageState('en');
      return;
    }

    const handleLanguageChange = (event: CustomEvent) => {
      setCurrentLanguageState(event.detail.language);
    };

    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
    };
  }, []);

  const setLanguage = useCallback((language: SupportedLanguage) => {
    if (!isLanguageSelectionEnabled) return;
    languageUtils.setLanguage(language);
    setCurrentLanguageState(language);
  }, []);

  const t = useCallback(
    (
      key: string,
      section:
        | 'getHelp'
        | 'myIssues'
        | 'reportIssue'
        | 'common'
        | 'aboutYou'
        | 'account'
        | 'settings'
        | 'header'
        | 'footer'
        | 'howItWorks'
        | 'safety'
        | 'hero'
        | 'statistics'
        | 'benefits'
        | 'testimonials'
        | 'topBar'
        | 'quickRideFinder'
        | 'appStore' = 'common'
    ): string => getTranslation(key, section as any),
    [currentLanguage]
  );

  const tArray = useCallback(
    (
      key: string,
      section:
        | 'getHelp'
        | 'myIssues'
        | 'reportIssue'
        | 'common'
        | 'aboutYou'
        | 'account'
        | 'settings'
        | 'header'
        | 'footer'
        | 'howItWorks'
        | 'safety'
        | 'hero'
        | 'statistics'
        | 'benefits'
        | 'testimonials'
        | 'topBar'
        | 'quickRideFinder'
        | 'appStore' = 'common'
    ): string[] => getTranslationArray(key, section as any),
    [currentLanguage]
  );

  return {
    currentLanguage,
    setLanguage,
    t,
    tArray,
    isLanguageSelectionEnabled,
    getLanguageName: languageUtils.getLanguageName,
    getSupportedLanguages: languageUtils.getSupportedLanguages,
  };
};
